import { Alert, Skeleton } from "antd"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

/** A browser (or embedded WebView) without an inline PDF renderer never
 * fires the iframe's load event — without a deadline the skeleton would
 * animate forever with no error and no download hint. */
const IFRAME_LOAD_TIMEOUT_MS = 8000

/**
 * A direct `<iframe src={fileUrl}>` looked like it should work — the browser
 * has a native PDF viewer — but the backend's media response carries
 * `X-Frame-Options: DENY` (Django's clickjacking middleware, applied to every
 * response including file serving). That header blocks *framing* only:
 * `<img>`/`<video>`/`<audio>` and `fetch()` are unaffected, which is exactly
 * why image/video/audio preview already worked while PDF alone did not.
 *
 * The fix reuses `downloadFile`'s own technique: fetch the bytes with
 * `fetch()` (not a frame navigation, so `X-Frame-Options` never applies),
 * hand the resulting `Blob` a same-origin `blob:` URL, and frame *that*
 * instead of the original response. Works identically in local dev and
 * production, and never depends on a URL being reachable by a third party.
 */
export function PdfViewer({
  fileUrl,
  name,
  onLoad
}: {
  fileUrl: string
  name: string
  onLoad: () => void
}) {
  const { t } = useTranslation()
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null

    setBlobUrl(null)
    setError(false)
    setReady(false)

    fetch(fileUrl, { cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch PDF")
        return response.blob()
      })
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setBlobUrl(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [fileUrl])

  // The blob is in hand but the frame never reported load — fall back to
  // the same unavailable state a failed fetch shows. Re-runs when `ready`
  // flips, which clears the pending timer on a successful load.
  useEffect(() => {
    if (!blobUrl || ready) return
    const timer = setTimeout(() => setError(true), IFRAME_LOAD_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [blobUrl, ready])

  const handleIframeLoad = () => {
    setReady(true)
    onLoad()
  }

  if (error) {
    return (
      <Alert
        message={t("filePreview.pdfUnavailableTitle")}
        description={t("filePreview.pdfUnavailableDescription")}
        type="warning"
        showIcon
      />
    )
  }

  return (
    <div style={{ height: "70vh", position: "relative" }}>
      {!ready && (
        <Skeleton.Node active className="size-full absolute inset-0" />
      )}
      {blobUrl && (
        <iframe
          src={`${blobUrl}#navpanes=0`}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            borderRadius: 6,
            opacity: ready ? 1 : 0,
            transition: "opacity 0.3s"
          }}
          title={name}
          onLoad={handleIframeLoad}
        />
      )}
    </div>
  )
}
